import { Capacitor } from "@capacitor/core";
import type { DateTime } from "luxon";
import { type FC, type PropsWithChildren, use, useCallback, useEffect, useState } from "react";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { type MaintenanceNotice, type MaintenanceNoticeResult, MaintenanceNoticeState } from "@/utils/maintenanceNotice";
import { MaintenanceScreen } from "./MaintenanceScreen";
import { useNoticeRecheckOnResume, usePlannedRecheck } from "./useMaintenanceRecovery";

type ShownScreen = { endsAt: DateTime | null };

const plannedScreen = (notice: MaintenanceNotice): ShownScreen => ({ endsAt: notice.endsAt });

// DEFAULT: an unreadable file on first load opens the app — the file can't be allowed to lock people out.
const initialScreen = (result: MaintenanceNoticeResult): ShownScreen | null =>
	result.state === MaintenanceNoticeState.Active ? plannedScreen(result.notice) : null;

type MaintenanceGateProps = PropsWithChildren<{
	/** Started before the first render, so the enclosing Suspense fallback covers the wait (capped at 3s). */
	initialNotice: Promise<MaintenanceNoticeResult>;
}>;

/**
 * Sits above every app provider, so while the maintenance screen shows the whole app is unmounted — no
 * query retries, no snackbars, no half-loaded pages. Maintenance is switched on and off by `maintenance.json`.
 */
export const MaintenanceGate: FC<MaintenanceGateProps> = ({ initialNotice, children }) => {
	const firstResult = use(initialNotice);
	const [screen, setScreen] = useState<ShownScreen | null>(() => initialScreen(firstResult));
	const { track } = useAnalytics();
	const appShowing = screen === null;

	useEffect(() => {
		if (!appShowing) track("maintenance_screen_shown", { platform: Capacitor.getPlatform() });
	}, [appShowing, track]);

	const reloadApp = useCallback(() => {
		track("maintenance_screen_recovered", { platform: Capacitor.getPlatform() });
		window.location.reload();
	}, [track]);

	const onPlannedRecheck = useCallback(
		(result: MaintenanceNoticeResult) => {
			// A still-active file may have moved its end time, so the shown time follows it.
			if (result.state === MaintenanceNoticeState.Active) setScreen(plannedScreen(result.notice));
			// Only a confirmed end reloads: a failed read mid-maintenance would otherwise send everyone into a
			// broken app. An unknown result keeps the screen up and the next re-read tries again.
			else if (result.state === MaintenanceNoticeState.None) reloadApp();
		},
		[reloadApp],
	);
	const showPlanned = useCallback((notice: MaintenanceNotice) => setScreen(plannedScreen(notice)), []);

	usePlannedRecheck(!appShowing, onPlannedRecheck);
	useNoticeRecheckOnResume(appShowing, showPlanned);

	if (screen) return <MaintenanceScreen endsAt={screen.endsAt} />;
	return children;
};
